import { useTranslation } from '@jq4race/i18n';
import { Button } from '@jq4race/ui';
import { Bell, X } from 'lucide-react';
import { useState } from 'react';

import { usePushNotifications } from '../../hooks/use-push-notifications';

const DISMISS_KEY = 'jq4-push-banner-dismissed';

/**
 * PushNotificationBanner — Escenario 5 (US-COMMS-004).
 *
 * Shown to authenticated operators when:
 *   - VITE_VAPID_PUBLIC_KEY is present (production) — the hook returns 'default' only then
 *   - Notification.permission is 'default' (not yet decided)
 *   - The user has not dismissed the banner this session
 *
 * Renders nothing when the hook reports a non-default status (disabled, unsupported,
 * granted, denied) or the user dismissed the banner. Safe to mount unconditionally
 * inside AuthenticatedLayout/AppShell.
 */
export function PushNotificationBanner() {
  const { t } = useTranslation('notifications');
  const { permissionStatus, requestPermission } = usePushNotifications();

  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.sessionStorage.getItem(DISMISS_KEY) === '1';
  });

  // Nothing to show when: permission is not 'default' (covers no-key → 'disabled'),
  // or the user already dismissed the banner.
  if (permissionStatus !== 'default' || dismissed) {
    return null;
  }

  function handleDismiss() {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // sessionStorage may be blocked in restricted environments; non-fatal.
    }
  }

  async function handleActivate() {
    await requestPermission();
    // After the browser dialog resolves, permissionStatus updates via the hook.
    // If granted → subscribed. If denied → banner disappears (permissionStatus !== 'default').
    // Dismiss the banner either way — the decision was made.
    handleDismiss();
  }

  return (
    <aside
      className="fixed inset-x-4 bottom-20 z-50 mx-auto max-w-sm rounded-xl border border-border bg-card shadow-lg"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label={t('bannerAria')}
    >
      <div className="flex items-start gap-3 p-4">
        <Bell className="mt-0.5 size-5 shrink-0 text-jq-gold-bright" aria-hidden />
        <div className="flex-1">
          <p className="jq-body text-foreground font-semibold">{t('title')}</p>
          <p className="mt-1 jq-body-sm text-muted-foreground">{t('description')}</p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="flex h-11 w-11 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          aria-label={t('dismissAria')}
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
      <div className="px-4 pb-4">
        <Button size="lg" className="w-full" onClick={() => void handleActivate()}>
          {t('enable')}
        </Button>
      </div>
    </aside>
  );
}
